import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useState, useTransition } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowDownUp, ArrowUp, SearchX } from 'lucide-react';
import { debounce } from 'lodash';

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { withSuspense } from 'src/hoc';
import {
  EscrowMembersOrderByEnum,
  QueryOrderEnum,
  InvitationStatusEnum,
  EscrowStatusEnum,
  UserRoleEnum,
} from 'src/graphql/schema';
import { EscrowMembersDocument } from 'src/graphql/actions/escrowMembers/escrowMembers.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from 'src/lib/utils';
import Paginator from '@/components/ui/paginator';
import { Button } from '@/components/ui/button';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';

import { AddMemberDialog } from '.';

import { MembersManagementContextProvider } from '../../Management/model';
import { RoleCell, RemoveMemberDialog, MembersManagementContainer, refetchEscrowMembers } from '../../Management';
import { getMemberSortPriority } from '../../Management/lib';

type IMemberRow = {
  id: string;
  user: {
    id: string;
    name: string;
    role: UserRoleEnum;
  };
  email: string;
  role: string;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const columnHelper = createColumnHelper<IMemberRow>();
const columns = [
  columnHelper.accessor('user', {
    header: 'Team member',
    cell: (props) => {
      const name = props.getValue().name;
      const email = props.row.original.email;
      const fallback = name?.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8 md:size-10 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-foreground font-medium line-clamp-1">{name}</span>
        </div>
      );
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    enableSorting: false,
  }),
  columnHelper.accessor('role', {
    header: 'Permission',
    cell: (props) => (
      <div className="flex justify-end">
        <RoleCell role={props.getValue()} id={props.row.original.id} canUpdate={props.row.original.action.canUpdate} />
      </div>
    ),
    meta: {
      headerClassName: 'text-right',
    },
    enableSorting: false,
  }),
  columnHelper.accessor('action', {
    header: '',
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
    enableResizing: true,
    cell: (props) =>
      props.getValue().canRemove ? (
        <RemoveMemberDialog id={props.row.original.id} successCallback={refetchEscrowMembers} />
      ) : null,
    enableSorting: false,
  }),
];

type IAddMemberProps = {
  escrowPartyId: string;
  canAdd: boolean;
  escrowInvitationStatus?: InvitationStatusEnum;
  escrowStatus: EscrowStatusEnum;
};

const AddMember = ({ escrowPartyId = '', canAdd = false, escrowInvitationStatus, escrowStatus }: IAddMemberProps) => {
  if (!canAdd) return null;

  const isDisabled =
    escrowInvitationStatus === InvitationStatusEnum.Rejected || escrowStatus === EscrowStatusEnum.Terminated;

  return (
    <div className="shrink-0">
      <AddMemberDialog isDisabled={isDisabled}>
        <MembersManagementContextProvider
          escrowPartyId={escrowPartyId}
          canCreateMember={canAdd}
          addSuccessCallback={refetchEscrowMembers}
          removeSuccessCallback={refetchEscrowMembers}
        >
          <MembersManagementContainer noteText="By default, the escrow creator and account owner have administrative permissions. All other team members must be granted access to the escrow for them to contribute. If a team member isn’t listed, first add them to the organization." />
        </MembersManagementContextProvider>
      </AddMemberDialog>
    </div>
  );
};

type Props = {
  escrowPartyId: string;
  canAdd?: boolean;
  escrowInvitationStatus?: InvitationStatusEnum;
  escrowStatus: EscrowStatusEnum;
};

const MembersViewContainer = ({ escrowPartyId, canAdd = false, escrowInvitationStatus, escrowStatus }: Props) => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isPending, startTransition] = useTransition();
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);
  const {
    data: { escrowMembers },
  } = useSuspenseQuery(EscrowMembersDocument, {
    variables: {
      escrowPartyId,
      page,
      perPage: 10,
      orderBy: EscrowMembersOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
      query: searchQuery,
    },
  });

  const membersMapped = useMemo(() => {
    const sortedNodes = escrowMembers.nodes.slice().sort((a, b) => {
      const priorityA = getMemberSortPriority(a.user, profile.id);
      const priorityB = getMemberSortPriority(b.user, profile.id);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((member) => ({
      id: member.id,
      user: {
        id: member.user.id,
        name: member.user.name || '-',
        role: member.user.role,
      },
      email: member.user.email || '-',
      role: member.role,
      action: {
        canUpdate: member.policy.update,
        canRemove: member.policy.remove,
      },
    }));
  }, [escrowMembers.nodes, profile.id]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onPageChange = (pageNumber: number) => startTransition(() => setPage(pageNumber));

  const onSearch = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => startTransition(() => setSearchQuery(event.target.value)),
    500,
  );

  const onClearSearch = () => setSearchQuery('');

  return (
    <section className="space-y-6">
      <div className="flex justify-between items-center gap-2">
        <div className="space-y-1">
          <h2 className="font-semibold text-base text-gray-700">Escrow members</h2>
          <p className="text-sm text-gray-500">
            The team members who have access to this escrow, and their respective permissions.
          </p>
        </div>
        <AddMember
          canAdd={canAdd}
          escrowPartyId={escrowPartyId}
          escrowInvitationStatus={escrowInvitationStatus}
          escrowStatus={escrowStatus}
        />
      </div>
      <InputSearch
        isLoading={false}
        placeholder="Search"
        className={cn('max-w-[373px]', {
          'pointer-events-none': escrowInvitationStatus === InvitationStatusEnum.Rejected,
        })}
        onChange={onSearch}
        onClear={onClearSearch}
      />
      <div
        className={cn('border border-border rounded-md text-foreground bg-white', {
          'opacity-70 pointer-events-none': escrowInvitationStatus === InvitationStatusEnum.Rejected,
        })}
      >
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-muted-foreground font-bold text-xs first:rounded-tl-md last:rounded-tr-md',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {header.column.getCanSort() ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={header.column.getToggleSortingHandler()}
                        className="-ml-3 text-muted-foreground! font-bold text-xs"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ArrowUp />,
                          desc: <ArrowDown />,
                        }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                      </Button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    'group/row',
                    shouldHighlightMemberRow(row.original.user, profile.id) && 'bg-slate-100 hover:bg-slate-100',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.columnDef.meta?.cellClassName,
                        'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                  <EmptyBlock
                    title="No results"
                    icon={SearchX}
                    withCard={false}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Paginator
        currentPage={page}
        totalPages={escrowMembers.pagesCount}
        onPageChange={onPageChange}
        showPreviousNext={true}
        hideOnSinglePage
        className="py-2"
      />
    </section>
  );
};

export default withSuspense(MembersViewContainer);

function shouldHighlightMemberRow({ id, role }: { id: string; role: UserRoleEnum }, currentUserId: string) {
  return id === currentUserId || role === UserRoleEnum.Owner;
}
